/* ---------------------------------------------------------------------------
 * Filter controls used in the Server Browser toolbar:
 *
 *   <DualRangeSlider min max lo hi onChange format />
 *     Two-handle slider with a live value tooltip above each handle.
 *
 *   <RangePopover pillIcon pillLabel unit panelTitle panelIcon min max lo hi
 *                 onChange format formatHeadline tickLabels tickGlyphs presets />
 *     Pill trigger that opens a styled popover with the slider, a big
 *     headline value, two numeric inputs, a Reset button, and preset chips.
 *
 * Look & feel matches the live dossier theme (var(--background-secondary),
 * var(--accent), monospace labels, soft accent glow in the corner of the
 * popover) — same chrome as the Trending cards.
 * ------------------------------------------------------------------------- */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'

// Dual-handle range slider with live tooltips. Snapshots min/max bounds and a
// formatter so the same component drives Players (0-128, integer) and In-game
// time (0-23, formatted as HH:00).
export function DualRangeSlider({
  min, max, lo, hi, onChange, format,
}: {
  min: number; max: number; lo: number; hi: number
  onChange: (lo: number, hi: number) => void
  format?: (v: number) => string
}) {
  const railRef = useRef<HTMLDivElement>(null)
  const [drag, setDrag] = useState<'lo' | 'hi' | null>(null)
  const fmt = format ?? ((v: number) => String(v))
  const lp = max === min ? 0   : ((lo - min) / (max - min)) * 100
  const hp = max === min ? 100 : ((hi - min) / (max - min)) * 100

  useEffect(() => {
    if (!drag) return
    const move = (e: PointerEvent) => {
      if (!railRef.current) return
      const rect = railRef.current.getBoundingClientRect()
      const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      const v = Math.round(min + pct * (max - min))
      if (drag === 'lo') onChange(Math.min(v, hi), hi)
      else               onChange(lo, Math.max(v, lo))
    }
    const up = () => setDrag(null)
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', up)
    return () => {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
    }
  }, [drag, hi, lo, max, min, onChange])

  return (
    <div ref={railRef} className="relative h-2 rounded-full bg-[var(--background-tertiary)] touch-none select-none my-3.5">
      <div className="absolute inset-y-0 rounded-full bg-[var(--accent)]/70"
        style={{ left: lp + '%', width: (hp - lp) + '%' }} />
      {(['lo', 'hi'] as const).map((side) => {
        const pct = side === 'lo' ? lp : hp
        const v = side === 'lo' ? lo : hi
        const isDrag = drag === side
        // Keyboard support: arrows nudge by 1, PageUp/Down by 10% of the range,
        // Home/End jump to the ends. The handles are real sliders to AT.
        const step = 1
        const bigStep = Math.max(step, Math.round((max - min) / 10))
        const setV = (nv: number) => {
          const c = Math.max(min, Math.min(max, nv))
          if (side === 'lo') onChange(Math.min(c, hi), hi)
          else onChange(lo, Math.max(c, lo))
        }
        const onKey = (e: React.KeyboardEvent) => {
          switch (e.key) {
            case 'ArrowLeft': case 'ArrowDown': setV(v - step); break
            case 'ArrowRight': case 'ArrowUp': setV(v + step); break
            case 'PageDown': setV(v - bigStep); break
            case 'PageUp': setV(v + bigStep); break
            case 'Home': setV(side === 'lo' ? min : lo); break
            case 'End': setV(side === 'lo' ? hi : max); break
            default: return
          }
          e.preventDefault()
        }
        return (
          <button key={side} type="button"
            role="slider" aria-orientation="horizontal"
            aria-label={side === 'lo' ? 'Minimum value' : 'Maximum value'}
            aria-valuemin={side === 'lo' ? min : lo} aria-valuemax={side === 'lo' ? hi : max}
            aria-valuenow={v} aria-valuetext={fmt(v)}
            onKeyDown={onKey}
            onPointerDown={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId); setDrag(side) }}
            className="group absolute w-[22px] h-[22px] rounded-full cursor-grab active:cursor-grabbing transition-transform hover:scale-110 before:content-[''] before:absolute before:-inset-3"
            style={{
              left: pct + '%', top: -7,
              transform: `translateX(-50%)${isDrag ? ' scale(1.12)' : ''}`,
              background: 'var(--foreground)',
              border: '3px solid var(--accent)',
              boxShadow: isDrag
                ? '0 0 0 6px rgba(255,255,255,0.16), 0 2px 8px rgba(0,0,0,0.45)'
                : '0 2px 6px rgba(0,0,0,0.4)',
            }}>
            <span aria-hidden
              className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 mono text-[11px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap pointer-events-none transition-opacity
                ${isDrag ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
              style={{ background: 'var(--accent)', color: 'var(--accent-foreground, #1a1810)' }}>
              {fmt(v)}
              <span aria-hidden className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent"
                style={{ borderTopColor: 'var(--accent)' }} />
            </span>
          </button>
        )
      })}
    </div>
  )
}

// Pill trigger + popover. The pill shows the current value in accent gold when
// the filter is set, or "any" in muted text when not. Popover closes on
// outside click or Escape.
// Position a popover relative to the VIEWPORT (fixed) and clamp it so it never
// runs off either edge — fixes left-anchored popovers overflowing for right-side
// triggers and on narrow/mobile screens. Returns null until measured.
export function usePopoverPosition(
  open: boolean,
  ref: { current: HTMLElement | null },
  maxWidth = 400,
): { top: number; left: number; width: number } | null {
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  useLayoutEffect(() => {
    if (!open || !ref.current) { setPos(null); return }
    const place = () => {
      const el = ref.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const margin = 12
      const width = Math.min(maxWidth, window.innerWidth - margin * 2)
      let left = r.left
      if (left + width > window.innerWidth - margin) left = window.innerWidth - margin - width
      if (left < margin) left = margin
      setPos({ top: r.bottom + 8, left, width })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true) }
  }, [open, ref, maxWidth])
  return pos
}

export function RangePopover({
  pillIcon, pillLabel, unit, panelTitle, panelIcon,
  min, max, lo, hi, onChange,
  format, formatHeadline, tickLabels, tickGlyphs, presets, experimental, note,
}: {
  pillIcon: ReactNode; pillLabel: string; unit?: string
  panelTitle: string; panelIcon: ReactNode
  min: number; max: number; lo: number; hi: number
  onChange: (lo: number, hi: number) => void
  format?: (v: number) => string
  formatHeadline?: (v: number) => string
  tickLabels?: string[]
  tickGlyphs?: ReactNode[]
  presets?: { label: string; lo: number; hi: number }[]
  experimental?: boolean
  note?: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const pos = usePopoverPosition(open, wrapRef)

  useEffect(() => {
    if (!open) return
    const click = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', click)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('mousedown', click)
      document.removeEventListener('keydown', key)
    }
  }, [open])

  const isSet = !(lo === min && hi === max)
  const fmt    = format         ?? ((v: number) => String(v))
  const fmtBig = formatHeadline ?? fmt

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button type="button" onClick={() => setOpen((o) => !o)}
        style={{ minHeight: 40 }}
        className={`mono text-xs uppercase tracking-wider rounded-full px-4 inline-flex items-center gap-2 transition-colors ${
          isSet
            ? 'bg-[var(--brand-soft)] text-[var(--brand)] font-semibold ring-1 ring-[var(--brand)] [--icon-accent:var(--brand)]'
            : 'bg-[var(--surface-1)] text-[var(--muted)] hover:text-[var(--foreground)] hover:bg-[var(--surface-1-hover)]'
        }`}>
        <span className="inline-flex">{pillIcon}</span>
        <span>{pillLabel}</span>
        <span className={isSet ? '' : 'text-[var(--foreground)] normal-case tracking-normal'}>
          {isSet ? `${fmt(lo)} – ${fmt(hi)}` : 'any'}
        </span>
      </button>
      {open && (
        <div role="dialog" className="fixed z-40 rounded-xl bg-[var(--background-secondary)] p-5"
          style={{ top: pos?.top ?? -9999, left: pos?.left ?? 0, width: pos?.width,
                   visibility: pos ? 'visible' : 'hidden', boxShadow: '0 18px 40px rgba(0,0,0,0.55)' }}>
          {/* head */}
          <div className="flex items-center justify-between mb-4 relative">
            <span className="mono text-[11px] uppercase tracking-wider text-[var(--muted)] inline-flex items-center gap-2">
              <span className="text-[var(--accent)] inline-flex">{panelIcon}</span>
              {panelTitle}
            </span>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close"
              className="w-6 h-6 rounded-full text-[var(--muted)] hover:text-[var(--foreground)] hover:bg-[var(--background-tertiary)] inline-flex items-center justify-center">
              ✕
            </button>
          </div>
          {/* big headline value */}
          <div className="mono text-[28px] leading-none mb-5 flex items-baseline gap-3">
            <span className="text-[var(--accent)]">{fmtBig(lo)}</span>
            <span className="text-[var(--muted)] text-[22px]">—</span>
            <span className="text-[var(--accent)]">{fmtBig(hi)}</span>
            {unit && <span className="mono text-[11px] uppercase tracking-wider text-[var(--muted)] ml-1">{unit}</span>}
          </div>
          {/* slider */}
          <DualRangeSlider min={min} max={max} lo={lo} hi={hi} format={fmtBig} onChange={onChange} />
          {/* ticks under the rail (glyphs or plain labels) */}
          {tickGlyphs && (
            <div className="flex justify-between mt-2 mono text-[10px] text-[var(--muted)]">
              {tickGlyphs.map((g, i) => <span key={i} className="inline-flex items-center gap-1">{g}</span>)}
            </div>
          )}
          {tickLabels && !tickGlyphs && (
            <div className="flex justify-between mt-1 mono text-[10px] text-[var(--muted)]">
              {tickLabels.map((l, i) => <span key={i}>{l}</span>)}
            </div>
          )}
          {/* input row */}
          <div className="flex items-center gap-2 mt-4">
            <input type="number" min={min} max={max} value={lo}
              onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) onChange(Math.max(min, Math.min(v, hi)), hi) }}
              className="w-16 h-8 rounded-lg bg-[var(--background-tertiary)] border-0 text-center mono text-[13px] text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]" />
            <span className="mono text-[11px] uppercase tracking-wider text-[var(--muted)]">to</span>
            <input type="number" min={min} max={max} value={hi}
              onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) onChange(lo, Math.max(lo, Math.min(v, max))) }}
              className="w-16 h-8 rounded-lg bg-[var(--background-tertiary)] border-0 text-center mono text-[13px] text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]" />
            <button type="button" onClick={() => onChange(min, max)}
              className="ml-auto mono text-[11px] uppercase tracking-wider rounded-full px-3 py-1.5 bg-[var(--background-tertiary)] text-[var(--muted)] hover:text-[var(--foreground)]">
              Reset
            </button>
          </div>
          {/* presets */}
          {presets && presets.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-4">
              <span className="mono text-[10px] uppercase tracking-wider text-[var(--muted)] mr-1">Quick</span>
              {presets.map((p) => {
                const active = p.lo === lo && p.hi === hi
                return (
                  <button key={p.label} type="button" onClick={() => onChange(p.lo, p.hi)}
                    className={`mono text-[11px] rounded-full px-2.5 py-1 transition-colors ${
                      active ? 'bg-[var(--accent)]/15 text-[var(--accent)]' : 'bg-[var(--background-tertiary)] text-[var(--muted)] hover:text-[var(--foreground)]'
                    }`}>{p.label}</button>
                )
              })}
            </div>
          )}
          {(experimental || note) && (
            <div className="mt-4 pt-3 border-t border-[var(--border)]">
              {experimental && (
                <span className="mono text-[9px] uppercase tracking-widest text-[var(--brand)] bg-[var(--brand-soft)] rounded px-1.5 py-0.5 mr-2">Experimental</span>
              )}
              {note && <span className="text-[11px] text-[var(--muted)] leading-snug">{note}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Inline segmented control (Mode / Perspective). First option = neutral "Any"
// state (subtle highlight); other options use the accent gold to signal "filter
// active". Generic over the value type.
export function Segmented<T extends string>({
  options, value, onChange, ariaLabel,
}: {
  options: { v: T; label: string }[]
  value: T
  onChange: (v: T) => void
  ariaLabel?: string
}) {
  return (
    // Height bumped to 40px to match the rest of the filter row after the
    // icon floor jumped to 24px — keeps Mode/Perspective in vertical rhythm
    // with the FilterPill chips and Players/In-game popovers.
    <div role="radiogroup" aria-label={ariaLabel} className="inline-flex bg-[var(--surface-1)] rounded-full p-1 gap-0.5" style={{ minHeight: 40 }}>
      {options.map((o, i) => {
        const selected = value === o.v
        const isAny = i === 0
        return (
          <button key={o.v} type="button" role="radio" aria-checked={selected}
            onClick={() => onChange(o.v)}
            className={`mono text-[11px] uppercase tracking-wider px-3.5 rounded-full inline-flex items-center transition-colors ${
              selected
                ? (isAny
                    ? 'bg-[var(--background-secondary)] text-[var(--foreground)]'
                    : 'bg-[var(--brand-soft)] text-[var(--brand)] font-semibold ring-1 ring-[var(--brand)] [--icon-accent:var(--brand)]')
                : 'text-[var(--muted)] hover:text-[var(--foreground)]'
            }`}>{o.label}</button>
        )
      })}
    </div>
  )
}

// Single circular icon toggle (used as the sun + moon "in-game time of day"
// pair). Active state lights up in accent gold.
export function IconToggle({
  icon, label, active, onClick,
}: { icon: ReactNode; label: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" aria-label={label} aria-pressed={active} title={label} onClick={onClick}
      className={`w-10 h-10 rounded-full inline-flex items-center justify-center transition-colors ${
        active
          ? 'bg-[var(--brand-soft)] text-[var(--brand)] ring-1 ring-[var(--brand)] [--icon-accent:var(--brand)]'
          : 'bg-[var(--surface-1)] text-[var(--muted)] hover:text-[var(--foreground)] hover:bg-[var(--surface-1-hover)]'
      }`}>
      {icon}
    </button>
  )
}
